import {Injectable} from '@angular/core';
import {AppConfigService} from '@npck/libs/commons';
import {v4 as uuidV4} from 'uuid';
import {EventRequest, Project} from '../apis/analytics-frontend-api';
import {SessionManager} from './analytics/session.manager';

const CAPTURE_URL = '/api/frontend/v1/analytics/events';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {

    private eventParametersFactory: () => JsonObject;
    private sendEventsUrl: string;

    constructor(
        private sessionManager: SessionManager,
    ) {
    }

    init(project: string, eventPropertiesFactory: () => JsonObject, userId: string = null): void {
        this.sendEventsUrl = `${CAPTURE_URL}?project=${project as Project}`;
        this.eventParametersFactory = eventPropertiesFactory;
        this.sessionManager.start(userId);
    }

    capture(eventName: string, properties: JsonObject = {}): void {
        if (!AppConfigService.appConfig.analyticsEnabled) {
            return;
        }

        const session = this.sessionManager.session;
        const eventTime = this.sessionManager.getCurrentTime();

        this.send({
            event_id: uuidV4(),
            session_id: session.id,
            user_id: session.userId,
            device_id: session.deviceId,
            device_type: session.deviceType,
            device_os: session.deviceOs,
            os_version: session.osVersion,
            version: AppConfigService.appConfig.frontendVersion,
            browser_version: session.browserVersion,
            screen: session.screen,
            language: session.language,
            referral: session.referral,
            session_start: session.sessionStart.toISOString(),
            session_time: eventTime.getTime() - session.sessionStart.getTime(),
            session_finish: session.sessionFinish?.toISOString(),
            event_time: eventTime.toISOString(),
            event_name: eventName,
            user_agent: session.userAgent,
            env: AppConfigService.appConfig.env,
            event_parameters: {
                ...(this.eventParametersFactory()),
                ...properties,
            } as EventRequest['event_parameters'],
        });
    }

    setUserId(userId: string): void {
        this.sessionManager.setUserId(userId);
    }

    finishSession(): void {
        this.sessionManager.finish();
    }

    private send(data: EventRequest): void {
        let body: string;
        try {
            body = JSON.stringify([data]);
        } catch (e) {
            console.error('Unable to serialize analytics event', e);
            return;
        }
        if (navigator.sendBeacon) {
            const blob = new Blob([body], {type: 'application/json'});
            navigator.sendBeacon(this.sendEventsUrl, blob) || console.error('Unable to queue analytics request');
        } else {
            fetch(
                this.sendEventsUrl,
                {
                    method: 'POST',
                    body,
                    headers: {'Content-Type': 'application/json'},
                },
            ).catch(e => console.error('Unable to send analytics request', e));
        }
    }
}

type JsonValue =
    | string
    | number
    | boolean
    | null
    | object
    | JsonValue[]
    | {[key: string]: JsonValue};


type JsonObject = {[key: string]: JsonValue};
